/**
 * Releases Page
 * 
 * Fetches and displays release notes from GitHub API or custom API
 */

import { APIClient } from './api-client';
import { showLoading, showError } from './utils';

export interface Release {
  id: string | number;
  tag_name: string;
  name: string;
  body: string;
  published_at: string;
  html_url: string;
  author?: {
    login: string;
    avatar_url: string;
  };
  assets?: Array<{
    name: string;
    download_count: number;
    browser_download_url: string;
  }>;
}

export interface FilterOptions {
  project?: string;
  versionType?: 'all' | 'major' | 'minor' | 'patch';
  dateRange?: 'all' | '30d' | '90d' | '6m' | '1y';
  search?: string;
}

export interface PaginationState {
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
  totalPages: number;
}

export class ReleasesManager {
  private container: HTMLElement;
  private api: APIClient;
  private apiEndpoint: string;
  private githubToken?: string;
  private allReleases: Release[] = [];
  private filteredReleases: Release[] = [];
  private filters: FilterOptions = {
    project: 'all',
    versionType: 'all',
    dateRange: 'all',
    search: ''
  };
  private pagination: PaginationState = {
    currentPage: 1,
    itemsPerPage: 10,
    totalItems: 0,
    totalPages: 0
  };

  constructor(containerId: string, apiEndpoint: string, githubToken?: string) {
    const container = document.getElementById(containerId);
    if (!container) {
      throw new Error(`Container with id "${containerId}" not found`);
    }

    this.container = container;
    this.api = new APIClient();
    this.apiEndpoint = apiEndpoint;
    this.githubToken = githubToken;
    
    // Initialize filter UI
    this.initializeFilters();
  }

  /**
   * Initialize filter UI and event listeners
   */
  private initializeFilters(): void {
    // Create filters container before the releases container
    const filtersHtml = this.renderFiltersUI();
    this.container.insertAdjacentHTML('beforebegin', filtersHtml);
    
    // Attach event listeners
    this.attachFilterListeners();
  }

  /**
   * Attach event listeners to filter controls
   */
  private attachFilterListeners(): void {
    // Project filter
    const projectFilter = document.getElementById('filter-project') as HTMLSelectElement;
    if (projectFilter) {
      projectFilter.addEventListener('change', (e) => {
        this.filters.project = (e.target as HTMLSelectElement).value;
        this.applyFilters();
      });
    }

    // Version type filter
    const versionFilter = document.getElementById('filter-version') as HTMLSelectElement;
    if (versionFilter) {
      versionFilter.addEventListener('change', (e) => {
        this.filters.versionType = (e.target as HTMLSelectElement).value as any;
        this.applyFilters();
      });
    }

    // Date range filter
    const dateFilter = document.getElementById('filter-date') as HTMLSelectElement;
    if (dateFilter) {
      dateFilter.addEventListener('change', (e) => {
        this.filters.dateRange = (e.target as HTMLSelectElement).value as any;
        this.applyFilters();
      });
    }

    // Search input
    const searchInput = document.getElementById('filter-search') as HTMLInputElement;
    if (searchInput) {
      searchInput.addEventListener('input', (e) => {
        this.filters.search = (e.target as HTMLInputElement).value;
        this.applyFilters();
      });
    }

    // Per page selector
    const perPageSelect = document.getElementById('items-per-page') as HTMLSelectElement;
    if (perPageSelect) {
      perPageSelect.addEventListener('change', (e) => {
        this.pagination.itemsPerPage = parseInt((e.target as HTMLSelectElement).value);
        this.pagination.currentPage = 1;
        this.applyFilters();
      });
    }
  }

  /**
   * Load and display releases
   */
  public async loadReleases(): Promise<void> {
    const resultsContainer = this.container;
    showLoading(resultsContainer);

    try {
      // Add GitHub token for private repos
      const options = this.githubToken ? {
        headers: {
          'Authorization': `token ${this.githubToken}`,
          'Accept': 'application/vnd.github.v3+json'
        }
      } : {
        headers: {
          'Accept': 'application/vnd.github.v3+json'
        }
      };

      const releases = await this.api.get<Release[]>(this.apiEndpoint, options);
      
      console.log('Releases fetched:', releases); // Debug log
      
      if (!releases || releases.length === 0) {
        this.renderNoReleases();
        return;
      }
      
      this.allReleases = releases;
      this.applyFilters();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      console.error('Failed to load releases:', error); // Debug log
      
      // Better error handling
      if (message.includes('404')) {
        this.render404Error();
      } else if (message.includes('401') || message.includes('403')) {
        this.renderAuthError();
      } else {
        showError(resultsContainer, `Failed to load releases: ${message}`);
      }
    }
  }

  /**
   * Apply filters and pagination
   */
  private applyFilters(): void {
    let filtered = [...this.allReleases];

    // Apply project filter
    if (this.filters.project && this.filters.project !== 'all') {
      filtered = filtered.filter(release => 
        release.tag_name.toLowerCase().includes(this.filters.project!.toLowerCase())
      );
    }

    // Apply version type filter
    if (this.filters.versionType && this.filters.versionType !== 'all') {
      filtered = filtered.filter(release => {
        const versionType = this.getVersionType(release.tag_name);
        return versionType === this.filters.versionType;
      });
    }

    // Apply date range filter
    if (this.filters.dateRange && this.filters.dateRange !== 'all') {
      const now = new Date();
      const cutoffDate = new Date();
      
      switch (this.filters.dateRange) {
        case '30d':
          cutoffDate.setDate(now.getDate() - 30);
          break;
        case '90d':
          cutoffDate.setDate(now.getDate() - 90);
          break;
        case '6m':
          cutoffDate.setMonth(now.getMonth() - 6);
          break;
        case '1y':
          cutoffDate.setFullYear(now.getFullYear() - 1);
          break;
      }
      
      filtered = filtered.filter(release => {
        const releaseDate = new Date(release.published_at);
        return releaseDate >= cutoffDate;
      });
    }

    // Apply search filter
    if (this.filters.search && this.filters.search.trim() !== '') {
      const searchLower = this.filters.search.toLowerCase();
      filtered = filtered.filter(release => 
        release.name?.toLowerCase().includes(searchLower) ||
        release.tag_name?.toLowerCase().includes(searchLower) ||
        release.body?.toLowerCase().includes(searchLower)
      );
    }

    this.filteredReleases = filtered;
    this.pagination.totalItems = filtered.length;
    this.pagination.totalPages = Math.ceil(filtered.length / this.pagination.itemsPerPage);
    
    // Reset to first page if current page is out of bounds
    if (this.pagination.currentPage > this.pagination.totalPages) {
      this.pagination.currentPage = 1;
    }

    this.renderFilteredReleases();
    this.updateResultsCount();
  }

  /**
   * Get version type from tag name
   */
  private getVersionType(tagName: string): 'major' | 'minor' | 'patch' {
    // Extract version number
    const versionMatch = tagName.match(/v?(\d+)\.(\d+)\.(\d+)/);
    if (!versionMatch) return 'patch';

    const [, major, minor, patch] = versionMatch;
    
    // Determine if it's a major, minor, or patch release
    if (patch !== '0') return 'patch';
    if (minor !== '0') return 'minor';
    return 'major';
  }

  /**
   * Update results count display
   */
  private updateResultsCount(): void {
    const countEl = document.getElementById('results-count');
    if (countEl) {
      const start = (this.pagination.currentPage - 1) * this.pagination.itemsPerPage + 1;
      const end = Math.min(start + this.pagination.itemsPerPage - 1, this.pagination.totalItems);
      
      countEl.textContent = this.pagination.totalItems === 0
        ? 'No releases found'
        : `Showing ${start}-${end} of ${this.pagination.totalItems} releases`;
    }
  }

  /**
   * Render authentication error
   */
  private renderAuthError(): void {
    this.container.innerHTML = `
      <div class="releases-error" style="background: #fef2f2; border-color: #fca5a5; padding: 3rem; text-align: center;">
        <h2 style="color: #dc2626; margin: 0 0 1rem 0;">🔒 Authentication Required</h2>
        <p style="color: #991b1b; margin: 0 0 2rem 0; font-size: 1.1rem;">
          This is a private repository. You need to provide a GitHub token.
        </p>
        
        <div style="background: white; border: 1px solid #fca5a5; border-radius: 0.5rem; padding: 2rem; max-width: 600px; margin: 0 auto; text-align: left;">
          <h3 style="margin: 0 0 1rem 0; color: #dc2626; font-size: 1rem;">How to fix:</h3>
          <ol style="margin: 0; color: #991b1b;">
            <li style="margin-bottom: 1rem;">
              <strong>Generate GitHub Token:</strong><br>
              <a href="https://github.com/settings/tokens/new" target="_blank" style="color: #dc2626;">
                https://github.com/settings/tokens/new
              </a>
            </li>
            <li style="margin-bottom: 1rem;">
              <strong>Select permissions:</strong><br>
              ✓ Check "repo" (Full control of private repositories)
            </li>
            <li style="margin-bottom: 1rem;">
              <strong>Copy the token</strong><br>
              Save it somewhere safe (you won't see it again!)
            </li>
            <li>
              <strong>Update releases.md:</strong><br>
              <code style="display: block; background: #fee; padding: 0.75rem; border-radius: 4px; margin-top: 0.5rem; font-size: 0.85rem;">
const GITHUB_TOKEN = 'your_token_here';<br>
const releases = new ReleasesManager(<br>
&nbsp;&nbsp;'releases-container',<br>
&nbsp;&nbsp;'https://api.github.com/repos/mitch1009/korena-ds/releases',<br>
&nbsp;&nbsp;GITHUB_TOKEN  // ← Pass the token<br>
);
              </code>
            </li>
          </ol>
        </div>

        <div style="margin-top: 2rem; padding: 1rem; background: #fff3cd; border: 1px solid #ffc107; border-radius: 0.5rem; max-width: 600px; margin-left: auto; margin-right: auto;">
          <p style="margin: 0; color: #856404; font-size: 0.9rem;">
            <strong>⚠️ Security Note:</strong> Don't commit your token to Git! 
            Use environment variables or config files (added to .gitignore).
          </p>
        </div>
      </div>
    `;
  }

  /**
   * Render 404 error with helpful message
   */
  private render404Error(): void {
    this.container.innerHTML = `
      <div class="releases-error" style="background: #fef2f2; border-color: #fca5a5; padding: 3rem; text-align: center;">
        <h2 style="color: #dc2626; margin: 0 0 1rem 0;">⚠️ Repository Not Found</h2>
        <p style="color: #991b1b; margin: 0 0 2rem 0; font-size: 1.1rem;">
          The GitHub repository doesn't exist or has no releases.
        </p>
        
        <div style="background: white; border: 1px solid #fca5a5; border-radius: 0.5rem; padding: 2rem; max-width: 600px; margin: 0 auto; text-align: left;">
          <h3 style="margin: 0 0 1rem 0; color: #dc2626; font-size: 1rem;">How to fix this:</h3>
          <ol style="margin: 0; color: #991b1b;">
            <li style="margin-bottom: 0.75rem;">
              <strong>Check repository exists:</strong><br>
              Visit the repository on GitHub
            </li>
            <li style="margin-bottom: 0.75rem;">
              <strong>Create a release:</strong><br>
              Go to <code>github.com/YOUR-REPO/releases/new</code>
            </li>
            <li style="margin-bottom: 0.75rem;">
              <strong>Update API endpoint:</strong><br>
              Edit <code>content/releases.md</code> with correct URL
            </li>
            <li>
              <strong>Example:</strong><br>
              <code style="display: block; background: #fee; padding: 0.5rem; border-radius: 4px; margin-top: 0.5rem;">
                api.github.com/repos/microsoft/vscode/releases
              </code>
            </li>
          </ol>
        </div>

        <div style="margin-top: 2rem;">
          <a href="https://docs.github.com/en/repositories/releasing-projects-on-github/managing-releases-in-a-repository" 
             target="_blank"
             class="download-btn"
             style="display: inline-flex;">
            📚 GitHub Releases Documentation
          </a>
        </div>
      </div>
    `;
  }

  /**
   * Render filters UI
   */
  private renderFiltersUI(): string {
    // Extract unique projects from tag names
    const projects = this.extractProjects();
    
    return `
      <div class="releases-filters fade-in">
        <div class="filters-header">
          <h3>Filter Releases</h3>
          <button id="reset-filters" class="btn-reset">Reset Filters</button>
        </div>
        
        <div class="filters-grid">
          <!-- Search -->
          <div class="filter-group filter-group--search">
            <label for="filter-search">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Search Releases
            </label>
            <input 
              type="text" 
              id="filter-search" 
              placeholder="Search by name, version, or content..."
              class="filter-input"
            />
          </div>

          <!-- Project Filter -->
          <div class="filter-group">
            <label for="filter-project">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
              </svg>
              Project
            </label>
            <select id="filter-project" class="filter-select">
              <option value="all">All Projects</option>
              ${projects.map(project => `
                <option value="${project.value}">${project.label}</option>
              `).join('')}
            </select>
          </div>

          <!-- Version Type Filter -->
          <div class="filter-group">
            <label for="filter-version">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              Version Type
            </label>
            <select id="filter-version" class="filter-select">
              <option value="all">All Versions</option>
              <option value="major">🔴 Major Releases</option>
              <option value="minor">🟡 Minor Releases</option>
              <option value="patch">🟢 Patch Releases</option>
            </select>
          </div>

          <!-- Date Range Filter -->
          <div class="filter-group">
            <label for="filter-date">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Date Range
            </label>
            <select id="filter-date" class="filter-select">
              <option value="all">All Time</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
              <option value="6m">Last 6 Months</option>
              <option value="1y">Last Year</option>
            </select>
          </div>

          <!-- Items Per Page -->
          <div class="filter-group">
            <label for="items-per-page">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              Per Page
            </label>
            <select id="items-per-page" class="filter-select">
              <option value="5">5</option>
              <option value="10" selected>10</option>
              <option value="20">20</option>
              <option value="50">50</option>
            </select>
          </div>
        </div>

        <div class="filters-results">
          <span id="results-count" class="results-count">Loading...</span>
        </div>
      </div>
    `;
  }

  /**
   * Extract unique projects from releases
   */
  private extractProjects(): Array<{value: string, label: string}> {
    const projectSet = new Set<string>();
    
    // Common project prefixes
    const prefixes = ['auth-service', 'yathu-website', 'docs', 'api-gateway', 'chat-service', 'mcp-service'];
    
    this.allReleases.forEach(release => {
      const tagName = release.tag_name.toLowerCase();
      const prefix = prefixes.find(p => tagName.includes(p));
      if (prefix) {
        projectSet.add(prefix);
      }
    });

    return Array.from(projectSet).map(project => ({
      value: project,
      label: project.split('-').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ')
    }));
  }

  /**
   * Render filtered and paginated releases
   */
  private renderFilteredReleases(): void {
    if (this.filteredReleases.length === 0) {
      this.container.innerHTML = `
        <div class="no-results">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3>No Releases Found</h3>
          <p>Try adjusting your filters or search query</p>
        </div>
      `;
      return;
    }

    // Get current page releases
    const startIndex = (this.pagination.currentPage - 1) * this.pagination.itemsPerPage;
    const endIndex = startIndex + this.pagination.itemsPerPage;
    const pageReleases = this.filteredReleases.slice(startIndex, endIndex);

    const html = pageReleases.map((release) => this.renderRelease(release)).join('');
    const paginationHtml = this.renderPagination();
    
    this.container.innerHTML = html + paginationHtml;
    
    // Attach pagination event listeners
    this.attachPaginationListeners();
  }

  /**
   * Render pagination controls
   */
  private renderPagination(): string {
    if (this.pagination.totalPages <= 1) {
      return '';
    }

    const { currentPage, totalPages } = this.pagination;
    const pages: (number | string)[] = [];

    // Always show first page
    pages.push(1);

    // Calculate range around current page
    const rangeStart = Math.max(2, currentPage - 2);
    const rangeEnd = Math.min(totalPages - 1, currentPage + 2);

    // Add ellipsis after first page if needed
    if (rangeStart > 2) {
      pages.push('...');
    }

    // Add pages in range
    for (let i = rangeStart; i <= rangeEnd; i++) {
      pages.push(i);
    }

    // Add ellipsis before last page if needed
    if (rangeEnd < totalPages - 1) {
      pages.push('...');
    }

    // Always show last page (if more than 1 page)
    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return `
      <div class="pagination fade-in">
        <button 
          class="pagination-btn pagination-btn--prev" 
          ${currentPage === 1 ? 'disabled' : ''}
          data-page="${currentPage - 1}"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
          Previous
        </button>

        <div class="pagination-pages">
          ${pages.map(page => {
            if (page === '...') {
              return '<span class="pagination-ellipsis">...</span>';
            }
            return `
              <button 
                class="pagination-page ${page === currentPage ? 'active' : ''}"
                data-page="${page}"
                ${page === currentPage ? 'disabled' : ''}
              >
                ${page}
              </button>
            `;
          }).join('')}
        </div>

        <button 
          class="pagination-btn pagination-btn--next" 
          ${currentPage === totalPages ? 'disabled' : ''}
          data-page="${currentPage + 1}"
        >
          Next
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    `;
  }

  /**
   * Attach pagination event listeners
   */
  private attachPaginationListeners(): void {
    const paginationButtons = document.querySelectorAll('[data-page]');
    paginationButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        const page = parseInt((e.currentTarget as HTMLElement).getAttribute('data-page') || '1');
        this.pagination.currentPage = page;
        this.renderFilteredReleases();
        this.updateResultsCount();
        
        // Scroll to top of releases
        this.container.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    // Reset filters button
    const resetBtn = document.getElementById('reset-filters');
    if (resetBtn) {
      resetBtn.addEventListener('click', () => {
        this.filters = {
          project: 'all',
          versionType: 'all',
          dateRange: 'all',
          search: ''
        };
        
        // Reset form elements
        (document.getElementById('filter-project') as HTMLSelectElement).value = 'all';
        (document.getElementById('filter-version') as HTMLSelectElement).value = 'all';
        (document.getElementById('filter-date') as HTMLSelectElement).value = 'all';
        (document.getElementById('filter-search') as HTMLInputElement).value = '';
        
        this.pagination.currentPage = 1;
        this.applyFilters();
      });
    }
  }

  /**
   * Render no releases found message
   */
  private renderNoReleases(): void {
    this.container.innerHTML = `
      <div style="text-align: center; padding: 4rem 2rem;">
        <div class="card" style="max-width: 600px; margin: 0 auto; padding: 3rem;">
          <h2 style="margin: 0 0 1rem 0;">📦 No Releases Yet</h2>
          <p style="color: var(--color-text-light); margin: 0 0 2rem 0;">
            This repository doesn't have any releases yet.
          </p>
          <a href="https://github.com/new/release" target="_blank" class="download-btn">
            Create Your First Release
          </a>
        </div>
      </div>
    `;
  }

  /**
   * Render releases
   */
  private renderReleases(releases: Release[]): void {
    if (releases.length === 0) {
      this.container.innerHTML = '<p style="text-align: center; padding: 2rem; color: var(--color-text-light);">No releases found</p>';
      return;
    }

    const html = releases.map((release) => this.renderRelease(release)).join('');
    this.container.innerHTML = html;
  }

  /**
   * Render a single release
   */
  private renderRelease(release: Release): string {
    // Safely handle dates
    const date = release.published_at 
      ? new Date(release.published_at).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      : 'Unknown date';

    // Safely handle body
    const body = release.body ? this.parseReleaseBody(release.body) : '<p>No description provided.</p>';

    // Safely handle tag_name
    const tagName = release.tag_name || 'Unknown';
    const version = tagName.replace(/^v/, '');

    return `
      <div class="release-card fade-in">
        <div class="release-header">
          <div class="release-version">
            <h2>${release.name || tagName}</h2>
            <span class="version-badge">v${version}</span>
          </div>
          <div class="release-meta">
            <span class="meta-item">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              ${date}
            </span>
            ${release.author ? `
              <a href="https://github.com/${release.author.login}" target="_blank" class="release-author">
                <img src="${release.author.avatar_url}" alt="${release.author.login}" class="author-avatar" />
                <span class="author-name">${release.author.login}</span>
              </a>
            ` : ''}
          </div>
        </div>

        <div class="release-body">
          ${body}
        </div>

        ${release.assets && release.assets.length > 0 ? `
          <div class="release-downloads">
            ${release.assets.slice(0, 3).map(asset => `
              <a href="${asset.browser_download_url || '#'}" class="download-btn download-btn--secondary">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                ${asset.name || 'Download'}
              </a>
            `).join('')}
          </div>
        ` : ''}

        ${release.html_url ? `
          <div style="margin-top: 1rem;">
            <a href="${release.html_url}" target="_blank" class="download-btn download-btn--secondary">
              View on GitHub →
            </a>
          </div>
        ` : ''}
      </div>
    `;
  }

  /**
   * Parse release body markdown to categorized sections
   */
  private parseReleaseBody(body: string): string {
    if (!body || body.trim() === '') {
      return '<p>No description provided.</p>';
    }

    // DON'T escape HTML yet - parse GitHub patterns first
    let html = body;

    // Parse full GitHub URLs FIRST (before escaping)
    html = html.replace(
      /https?:\/\/github\.com\/([^\/]+)\/([^\/]+)\/(pull|issues|commit)\/([a-zA-Z0-9]+)/g,
      (match, owner, repo, type, number) => {
        const icon = type === 'pull' ? '🔀' : type === 'issues' ? '📋' : '💾';
        const label = type === 'pull' ? 'PR' : type === 'issues' ? 'Issue' : 'Commit';
        const displayText = type === 'commit' ? number.substring(0, 7) : `#${number}`;
        return `__GITHUB_LINK__${icon} ${label} ${displayText}__${match}__GITHUB_LINK__`;
      }
    );

    // Parse PR/Issue links (#123) - must be preceded by space or start of line
    const repoMatch = this.apiEndpoint.match(/repos\/([^\/]+)\/([^\/]+)/);
    if (repoMatch) {
      const [, owner, repo] = repoMatch;
      html = html.replace(/(\s|^)#(\d+)(\s|$|\.)/g, 
        (match, before, number, after) => 
          `${before}__PR_LINK__${number}__${owner}__${repo}__PR_LINK__${after}`
      );
    }

    // Parse GitHub mentions (@username)
    html = html.replace(/(\s|^)@([a-zA-Z0-9_-]+)(\s|$)/g, 
      (match, before, username, after) =>
        `${before}__MENTION__${username}__MENTION__${after}`
    );

    // NOW escape HTML to prevent XSS
    const escapeHtml = (text: string): string => {
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    };

    html = escapeHtml(html);

    // Replace placeholders with actual HTML links (AFTER escaping)
    // GitHub links
    html = html.replace(/__GITHUB_LINK__([^_]+)__([^_]+)__GITHUB_LINK__/g, 
      (match, display, url) => 
        `<a href="${url}" target="_blank" class="github-link" title="${url}">${display}</a>`
    );

    // PR links
    html = html.replace(/__PR_LINK__(\d+)__([^_]+)__([^_]+)__PR_LINK__/g,
      (match, number, owner, repo) =>
        `<a href="https://github.com/${owner}/${repo}/pull/${number}" target="_blank" class="pr-link">#${number}</a>`
    );

    // Mentions
    html = html.replace(/__MENTION__([^_]+)__MENTION__/g,
      (match, username) =>
        `<a href="https://github.com/${username}" target="_blank" class="contributor-mention">@${username}</a>`
    );

    // Headers
    html = html.replace(/^### (.*$)/gim, '<h4>$1</h4>');
    html = html.replace(/^## (.*$)/gim, '<h3>$1</h3>');

    // Bold
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

    // Lists with better formatting
    const lines = html.split('\n');
    let inList = false;
    const processedLines: string[] = [];

    lines.forEach(line => {
      if (line.match(/^[\*\-] /)) {
        if (!inList) {
          processedLines.push('<ul class="release-list">');
          inList = true;
        }
        const content = line.replace(/^[\*\-] /, '');
        processedLines.push('<li>' + content + '</li>');
      } else {
        if (inList) {
          processedLines.push('</ul>');
          inList = false;
        }
        processedLines.push(line);
      }
    });

    if (inList) {
      processedLines.push('</ul>');
    }

    html = processedLines.join('\n');

    // Line breaks
    html = html.replace(/\n\n/g, '</p><p>');
    html = '<p>' + html + '</p>';

    // Code blocks
    html = html.replace(/```(.*?)```/gs, '<pre><code>$1</code></pre>');
    
    // Inline code
    html = html.replace(/`(.*?)`/g, '<code>$1</code>');

    return html;
  }
}

